import { useState, useEffect, useRef } from 'react';

const DISMISS_RECHECK_SECS = 15 * 60;
const STORAGE_KEY = '__pwaUpdateDismissedAt';

function hasPendingUpdate() {
    if (!('serviceWorker' in navigator)) return Promise.resolve(false);
    return navigator.serviceWorker.getRegistration().then(reg => {
        return !!(reg?.waiting || reg?.installing);
    });
}

function getDismissTimestamp() {
    return parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
}

function setDismissTimestamp() {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
}

function shouldShowOnRecheck() {
    const dismissed = getDismissTimestamp();
    if (!dismissed) return true;
    return (Date.now() - dismissed) > DISMISS_RECHECK_SECS * 1000;
}

export function usePwaUpdate() {
    const [show, setShow] = useState(false);
    const waitingWorkerRef = useRef<ServiceWorker | null>(null);

    const handleReload = () => {
        setShow(false);
        const waitingWorker = waitingWorkerRef.current;

        // No waiting worker -> nothing to activate; just reload.
        if (!waitingWorker) {
            window.location.reload();
            return;
        }

        let reloaded = false;
        const reloadOnce = () => {
            if (reloaded) return;
            reloaded = true;
            navigator.serviceWorker.removeEventListener('controllerchange', reloadOnce);
            // Only reload once the NEW service worker has taken control.
            // Reloading earlier would be served by the OLD worker and we'd
            // see the previous version again (the original bug).
            window.location.reload();
        };

        navigator.serviceWorker.addEventListener('controllerchange', reloadOnce);
        // Safety net in case controllerchange never fires.
        setTimeout(reloadOnce, 3000);

        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    };

    const handleDismiss = () => {
        setShow(false);
        setDismissTimestamp();
    };

    const triggerUpdate = (reg) => {
        if (reg?.waiting) {
            waitingWorkerRef.current = reg.waiting;
        }
        setShow(true);
    };

    useEffect(() => {
        // The global registration in index.tsx dispatches this event when an
        // update is found; this hook only listens for it here.
        const onUpdateAvailable = (e: Event) => triggerUpdate((e as CustomEvent).detail);
        window.addEventListener('sw-update-available', onUpdateAvailable);

        hasPendingUpdate().then(hasUpdate => {
            if (hasUpdate && shouldShowOnRecheck()) {
                navigator.serviceWorker.getRegistration().then(reg => {
                    if (reg?.waiting) {
                        triggerUpdate(reg);
                    }
                });
            }
        });

        return () => window.removeEventListener('sw-update-available', onUpdateAvailable);
    }, []);

    return { show, handleDismiss, handleReload };
}
